import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Form, Input, Button, Card, Select, Switch, Space,
    InputNumber, Typography, message, Spin
} from 'antd';
import {
    ArrowLeftOutlined, SaveOutlined, PlusOutlined,
    MinusCircleOutlined, ToolOutlined
} from '@ant-design/icons';
import { servicesAPI } from '../services/api';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

export default function ServiceForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const fetchService = useCallback(async () => {
        if (!id) return;

        try {
            setLoading(true);
            const response = await servicesAPI.getById(id);
            form.setFieldsValue({
                ...response.data,
                features: response.data.features || ['']
            });
        } catch (error) {
            console.error('Error fetching service:', error);
            message.error('Failed to fetch service');
        } finally {
            setLoading(false);
        }
    }, [id, form]);

    useEffect(() => {
        if (isEdit) {
            fetchService();
        }
    }, [isEdit, fetchService]);

    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);

        try {
            const dataToSubmit = {
                ...values,
                slug: values.slug || generateSlug(values.title),
                features: values.features?.filter(f => f.trim()) || []
            };

            if (isEdit) {
                await servicesAPI.update(id, dataToSubmit);
                message.success('Service updated successfully!');
            } else {
                await servicesAPI.create(dataToSubmit);
                message.success('Service created successfully!');
            }
            navigate('/services');
        } catch (error) {
            message.error('Error: ' + error.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" tip="Loading service..." />
            </div>
        );
    }

    return (
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
            <div style={{ marginBottom: 24 }}>
                <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate('/services')}
                    style={{ marginBottom: 16 }}
                >
                    Back to Services
                </Button>
                <Title level={2}>
                    <ToolOutlined /> {isEdit ? 'Edit Service' : 'Add New Service'}
                </Title>
            </div>

            <Card
                style={{
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    borderRadius: '8px'
                }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        category: 'design',
                        order: 0,
                        isActive: true,
                        features: ['']
                    }}
                >
                    <Form.Item
                        name="title"
                        label="Service Title"
                        rules={[{ required: true, message: 'Title is required!' }]}
                    >
                        <Input
                            size="large"
                            placeholder="e.g., Design & Planning"
                        />
                    </Form.Item>

                    <Form.Item
                        name="slug"
                        label="Slug"
                        tooltip="URL-friendly version of title (auto-generated if empty)"
                    >
                        <Input
                            size="large"
                            placeholder="design-planning"
                        />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description"
                        rules={[{ required: true, message: 'Description is required!' }]}
                    >
                        <TextArea
                            rows={4}
                            placeholder="Brief description of the service"
                        />
                    </Form.Item>

                    <Form.Item
                        name="category"
                        label="Category"
                    >
                        <Select size="large">
                            <Option value="design">Design & Planning</Option>
                            <Option value="structure">Structure Specialist</Option>
                            <Option value="slf-pbg">SLF & PBG</Option>
                            <Option value="testing">Testing</Option>
                            <Option value="soil">Soil Investigation</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="icon"
                        label="Icon Name"
                        tooltip="Icon identifier for display"
                    >
                        <Input
                            size="large"
                            placeholder="compass, building, clipboard-check"
                        />
                    </Form.Item>

                    <Form.Item
                        name="image"
                        label="Image URL"
                    >
                        <Input
                            size="large"
                            placeholder="https://example.com/image.jpg"
                        />
                    </Form.Item>

                    <Form.Item label="Features">
                        <Form.List name="features">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Space
                                            key={key}
                                            style={{ display: 'flex', marginBottom: 8 }}
                                            align="baseline"
                                        >
                                            <Form.Item
                                                {...restField}
                                                name={name}
                                                style={{ marginBottom: 0, flex: 1 }}
                                            >
                                                <Input
                                                    size="large"
                                                    placeholder="Feature name"
                                                />
                                            </Form.Item>
                                            {fields.length > 1 && (
                                                <MinusCircleOutlined
                                                    onClick={() => remove(name)}
                                                    style={{ color: '#ff4d4f', fontSize: 20 }}
                                                />
                                            )}
                                        </Space>
                                    ))}
                                    <Button
                                        type="dashed"
                                        onClick={() => add()}
                                        icon={<PlusOutlined />}
                                        style={{ width: '100%', marginTop: 8 }}
                                    >
                                        Add Feature
                                    </Button>
                                </>
                            )}
                        </Form.List>
                    </Form.Item>

                    <Form.Item
                        name="order"
                        label="Display Order"
                    >
                        <InputNumber
                            size="large"
                            min={0}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="isActive"
                        label="Status"
                        valuePropName="checked"
                    >
                        <Switch
                            checkedChildren="Active"
                            unCheckedChildren="Inactive"
                        />
                    </Form.Item>

                    <Form.Item style={{ marginTop: 32 }}>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button
                                size="large"
                                onClick={() => navigate('/services')}
                                style={{ minWidth: 120 }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                size="large"
                                htmlType="submit"
                                icon={<SaveOutlined />}
                                loading={submitting}
                                style={{
                                    minWidth: 120,
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    border: 'none',
                                }}
                            >
                                {isEdit ? 'Update' : 'Create'} Service
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
}
